import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { monthName, type PassportCountryGroup as PassportCountryGroupData, type PassportEntryRow } from '@/lib/passport';

interface PassportCountryGroupProps {
  group: PassportCountryGroupData;
  onEdit: (entry: PassportEntryRow) => void;
  onDelete: (entry: PassportEntryRow) => void;
}

const PassportCountryGroup = ({ group, onEdit, onDelete }: PassportCountryGroupProps) => {
  return (
    <section id={`passport-country-${group.countryIso2 ?? group.countryId}`} className="scroll-mt-24 py-8">
      <div className="mb-5">
        <h3 className="font-serif text-xl md:text-2xl text-foreground">{group.countryName}</h3>
        <p className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">
          {group.entries.length} destination{group.entries.length === 1 ? '' : 's'}
        </p>
      </div>

      <ul>
        {group.entries.map((entry) => (
          <li
            key={entry.id}
            className="group flex items-center justify-between gap-3 rounded-md py-1.5 px-2 -mx-2 hover:bg-secondary/40"
          >
            <div className="flex min-w-0 items-baseline gap-4">
              <span className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                {monthName(entry.month)}
              </span>
              <span className="truncate text-foreground">{entry.destination_name}</span>
            </div>
            <div className="flex shrink-0 items-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Edit ${entry.destination_name}, ${monthName(entry.month)}`}
                onClick={() => onEdit(entry)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete ${entry.destination_name}, ${monthName(entry.month)}`}
                onClick={() => onDelete(entry)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <Separator className="mt-8" />
    </section>
  );
};

export default PassportCountryGroup;
